import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
    Card,
    CardBody,
    Button,
    Input,
    Textarea,
    Divider,
    Avatar,
    Chip,
    Select,
    SelectItem,
    Form,
    DatePicker,
    Autocomplete,
    AutocompleteItem,
    Skeleton,
    NumberInput,
    useDisclosure,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "@heroui/react";
import {
    LuPackage,
    LuCircleAlert,
    LuArrowLeft,
    LuSearch,
} from "react-icons/lu";
import { parseDate } from "@internationalized/date";
import { PageHeader, ThemedText, NoImageFallback } from "@/components/ui";
import { useFetchData, usePostData } from "@/hooks";
import {
    showToast,
    clearToast,
    formatCurrency,
    formatDate,
    formatPrice,
} from "@/utils";
import { ReturnSummary } from "./components";

// Nilai retur per baris dibulatkan ke rupiah (harga jual bisa 2 desimal, mis. 333,33)
const calcReturnLineAmount = (quantity, price) =>
    Math.round((Number(quantity) || 0) * (Number(price) || 0));

const SalesOrderReturnCreate = () => {
    const navigate = useNavigate();
    const [selectedSI, setSelectedSI] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const { isOpen, onOpen, onOpenChange } = useDisclosure();

    // React Hook Form
    const {
        control,
        handleSubmit,
        setValue,
        watch,
        register,
        getValues,
        formState: { errors, isSubmitting, isValid },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            return_date: new Date().toISOString().split("T")[0],
            sales_order_id: "",
            description: "",
            items: [],
        },
    });

    const { fields, replace } = useFieldArray({
        control,
        name: "items",
    });

    const watchItems = watch("items");

    const filteredFields = useMemo(() => {
        return fields
            .map((field, index) => ({ field, index }))
            .filter(({ field }) =>
                field.material_name
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()),
            );
    }, [fields, searchQuery]);

    // Fetch Data
    const { data: salesOrders, isLoading: isLoadingSI } =
        useFetchData("select/sales-order");
    const { data: warehouses } = useFetchData("select/warehouse");

    // Submit Hook
    const { mutate: submit, isLoading: isSaving } = usePostData(
        "sales-order-returns/store",
    );

    // Handle SI Selection
    const handleSelectSI = (siId) => {
        if (!siId) {
            setSelectedSI(null);
            replace([]);
            setValue("sales_order_id", "");
            return;
        }

        const si = salesOrders?.find((s) => String(s.value) === String(siId));
        if (si) {
            setValue("sales_order_id", siId);
        }
    };

    // Fetch SI Details when ID changes
    const salesOrderId = watch("sales_order_id");
    const { data: siDetail, isLoading: isLoadingDetail } = useFetchData(
        salesOrderId ? `sales-orders/${salesOrderId}/show` : null,
    );

    // Populate Items when Detail Arrives
    useEffect(() => {
        if (siDetail && siDetail.items) {
            setSelectedSI(siDetail);
            const initialItems = siDetail.items.map((item) => ({
                sales_order_item_id: item.id,
                material_name: item.material?.name,
                material_unit_name: item.unit?.name,
                material_id: item.material_id,
                material_unit_id: item.material_unit_id,
                qty_sold: item.quantity,
                price: item.price,
                quantity: null,
                warehouse_id: null,
                description: "",
                image_url: item.material?.images?.[0]?.url_600,
            }));
            replace(initialItems);
        }
    }, [siDetail, replace]);

    // Calculate Total Return Amount (direct calculation, not memoized)
    // Note: useMemo doesn't work well here because watchItems reference may not change
    const totalReturnAmount = (watchItems || []).reduce((acc, item, index) => {
        return acc + calcReturnLineAmount(item?.quantity, fields[index]?.price);
    }, 0);

    const activeItemCount = (watchItems || []).filter(
        (i) => Number(i.quantity) > 0,
    ).length;

    const onSubmit = (data) => {
        const itemsToReturn = data.items.filter(
            (item) => Number(item.quantity) > 0,
        );

        if (itemsToReturn.length === 0) {
            showToast("error", "Gagal", "Minimal satu barang harus diretur");
            return;
        }

        const invalidItems = itemsToReturn.some((item) => !item.warehouse_id);
        if (invalidItems) {
            showToast(
                "error",
                "Gagal",
                "Gudang tujuan harus dipilih untuk barang yang diretur",
            );
            return;
        }

        onOpen();
    };

    const handleConfirm = () => {
        const data = getValues();
        const itemsToReturn = data.items.filter(
            (item) => Number(item.quantity) > 0,
        );

        const totalReturnAmount = itemsToReturn.reduce((acc, item) => {
            return acc + calcReturnLineAmount(item.quantity, item.price);
        }, 0);

        const payload = {
            ...data,
            items: itemsToReturn,
            return_amount: totalReturnAmount,
        };

        submit(payload, {
            onSuccess: () => {
                clearToast();
                showToast(
                    "success",
                    "Berhasil",
                    "Retur penjualan berhasil dibuat",
                );
                navigate("/sales/return");
                onOpenChange(false);
            },
            onError: (error) => {
                showToast(
                    "error",
                    "Gagal",
                    error.response?.data?.message ||
                        "Terjadi kesalahan saat menyimpan retur",
                );
            },
        });
    };

    return (
        <div className="flex h-full w-full flex-col gap-6">
            {/* Header */}
            <div className="flex gap-4">
                <Button
                    isIconOnly
                    variant="light"
                    onPress={() => navigate("/sales/return")}
                >
                    <LuArrowLeft size={20} />
                </Button>
                <PageHeader
                    title="Buat Retur Penjualan"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Sales" },
                        { label: "Retur Penjualan", href: "/sales/return" },
                        { label: "Buat Retur" },
                    ]}
                />
            </div>

            <Form
                className="flex w-full flex-col gap-6 lg:flex-row lg:items-start"
                onSubmit={handleSubmit(onSubmit)}
            >
                {/* LEFT PANEL: INFO & CONTROL */}
                <div className="flex w-full flex-col gap-4 lg:sticky lg:top-6 lg:w-1/3">
                    <Card className="border-default-200 border" shadow="none">
                        <CardBody className="flex flex-col gap-5 p-5">
                            <ThemedText className="font-semibold">
                                Informasi Order
                            </ThemedText>

                            {/* Select SI */}
                            <Controller
                                name="sales_order_id"
                                control={control}
                                rules={{
                                    required: "Sales Order wajib dipilih",
                                }}
                                render={({ field }) => (
                                    <Autocomplete
                                        isRequired
                                        label="Sales Order"
                                        placeholder="Ketik No. Order / Pelanggan..."
                                        isLoading={isLoadingSI}
                                        selectedKey={
                                            field.value
                                                ? String(field.value)
                                                : null
                                        }
                                        onSelectionChange={handleSelectSI}
                                        isInvalid={!!errors.sales_order_id}
                                        errorMessage={
                                            errors.sales_order_id?.message
                                        }
                                        variant="bordered"
                                    >
                                        {(salesOrders || []).map((so) => (
                                            <AutocompleteItem
                                                key={so.value}
                                                textValue={so.title}
                                            >
                                                {so.title}
                                            </AutocompleteItem>
                                        ))}
                                    </Autocomplete>
                                )}
                            />

                            {/* SI Detail Preview */}
                            {selectedSI && (
                                <div className="flex flex-col gap-2 rounded-lg border border-dashed border-zinc-200 bg-zinc-50/50 p-4 dark:border-zinc-700 dark:bg-zinc-900/20">
                                    <div className="flex justify-between">
                                        <ThemedText
                                            size="xs"
                                            variant="secondary"
                                        >
                                            Pelanggan
                                        </ThemedText>
                                        <ThemedText
                                            size="sm"
                                            className="text-right font-medium"
                                        >
                                            {selectedSI.customer?.name}
                                        </ThemedText>
                                    </div>
                                    <div className="flex justify-between">
                                        <ThemedText
                                            size="xs"
                                            variant="secondary"
                                        >
                                            Tanggal Order
                                        </ThemedText>
                                        <ThemedText
                                            size="sm"
                                            className="font-medium"
                                        >
                                            {formatDate(selectedSI.order_date)}
                                        </ThemedText>
                                    </div>
                                    <div className="flex justify-between">
                                        <ThemedText
                                            size="xs"
                                            variant="secondary"
                                        >
                                            Total Order
                                        </ThemedText>
                                        <ThemedText
                                            size="sm"
                                            className="font-medium"
                                        >
                                            {formatCurrency(
                                                selectedSI.total_amount,
                                            )}
                                        </ThemedText>
                                    </div>
                                    <Divider className="my-1" />
                                    <div className="flex items-center justify-between">
                                        <ThemedText
                                            size="xs"
                                            variant="secondary"
                                        >
                                            Status Pembayaran
                                        </ThemedText>
                                        <Chip
                                            size="sm"
                                            variant="flat"
                                            color={
                                                selectedSI.payment_status
                                                    ?.badge_color
                                            }
                                        >
                                            {selectedSI.payment_status?.name}
                                        </Chip>
                                    </div>
                                </div>
                            )}

                            {/* Return Date */}
                            <Controller
                                name="return_date"
                                control={control}
                                rules={{ required: "Tanggal wajib diisi" }}
                                render={({ field }) => (
                                    <DatePicker
                                        isRequired
                                        label="Tanggal Retur"
                                        value={
                                            field.value
                                                ? parseDate(field.value)
                                                : null
                                        }
                                        onChange={(date) =>
                                            field.onChange(date.toString())
                                        }
                                        errorMessage={
                                            errors.return_date?.message
                                        }
                                        isInvalid={!!errors.return_date}
                                        variant="bordered"
                                        showMonthAndYearPickers
                                    />
                                )}
                            />

                            {/* Description */}
                            <Controller
                                name="description"
                                control={control}
                                render={({ field }) => (
                                    <Textarea
                                        {...field}
                                        label="Keterangan/Alasan Retur"
                                        placeholder="Contoh: Barang cacat produksi/rusak saat pengiriman"
                                        variant="bordered"
                                    />
                                )}
                            />
                        </CardBody>
                    </Card>

                    {/* Summary Card (Desktop Only) */}
                    <ReturnSummary
                        className="hidden lg:flex"
                        totalReturnAmount={totalReturnAmount}
                        activeItemCount={activeItemCount}
                        isValid={isValid}
                        isSaving={isSaving}
                        isSubmitting={isSubmitting}
                    />
                </div>

                {/* RIGHT PANEL: ITEMS */}
                <Card
                    className="border-default-200 w-full flex-1 border"
                    shadow="none"
                >
                    {salesOrderId && (
                        <div className="border-default-100 flex items-center justify-between border-b px-6 py-4">
                            <div className="flex flex-col gap-1">
                                <ThemedText size="sm" className="font-semibold">
                                    Daftar Barang{" "}
                                    {!isLoadingDetail &&
                                        fields.length > 0 &&
                                        `(${fields.length})`}
                                </ThemedText>
                                <ThemedText size="xs" variant="secondary">
                                    Isi Jumlah Retur {">"} 0 untuk memilih
                                    barang
                                </ThemedText>
                            </div>
                            <div className="w-52">
                                <Input
                                    placeholder="Cari barang..."
                                    size="sm"
                                    startContent={<LuSearch opacity={0.5} />}
                                    isClearable
                                    value={searchQuery}
                                    onValueChange={setSearchQuery}
                                    classNames={{
                                        inputWrapper: "bg-default-100",
                                    }}
                                />
                            </div>
                        </div>
                    )}
                    <CardBody
                        className={`flex flex-col p-0 ${
                            !salesOrderId ||
                            (!isLoadingDetail &&
                                selectedSI &&
                                fields.length === 0)
                                ? "min-h-[400px]"
                                : ""
                        }`}
                    >
                        {!salesOrderId ? (
                            <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
                                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-zinc-100 text-zinc-400 dark:bg-zinc-800">
                                    <LuPackage size={32} />
                                </div>
                                <div className="space-y-1">
                                    <ThemedText className="font-medium">
                                        Belum ada barang
                                    </ThemedText>
                                    <ThemedText
                                        variant="secondary"
                                        size="sm"
                                        className="max-w-xs"
                                    >
                                        Silakan pilih Sales Order di panel kiri
                                        untuk memuat daftar barang yang bisa
                                        diretur.
                                    </ThemedText>
                                </div>
                            </div>
                        ) : isLoadingDetail || !selectedSI ? (
                            <div className="flex h-full flex-col gap-4 p-6">
                                {[...Array(5)].map((_, i) => (
                                    <div key={i} className="flex gap-4">
                                        <Skeleton className="h-12 w-12 rounded-lg" />
                                        <div className="flex-1 space-y-2">
                                            <Skeleton className="h-4 w-3/4 rounded-lg" />
                                            <Skeleton className="h-3 w-1/2 rounded-lg" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : fields.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-zinc-500">
                                <LuCircleAlert size={24} className="mb-2" />
                                <ThemedText>
                                    Tidak ada item pada order ini
                                </ThemedText>
                            </div>
                        ) : filteredFields.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-zinc-500">
                                <LuSearch
                                    size={32}
                                    className="mb-2 opacity-20"
                                />
                                <ThemedText className="opacity-60">
                                    Tidak ditemukan "{searchQuery}"
                                </ThemedText>
                            </div>
                        ) : (
                            <div className="divide-default-100 divide-y">
                                {filteredFields.map(({ field, index }) => {
                                    const isActive =
                                        Number(watchItems[index]?.quantity) > 0;
                                    const qtySold = Number(field.qty_sold);

                                    return (
                                        <div
                                            key={field.id}
                                            className="group flex flex-col gap-4 p-5 transition-colors"
                                        >
                                            {/* Hidden Fields for Submission */}
                                            <input
                                                type="hidden"
                                                {...register(
                                                    `items.${index}.sales_order_item_id`,
                                                )}
                                            />
                                            <input
                                                type="hidden"
                                                {...register(
                                                    `items.${index}.material_id`,
                                                )}
                                            />
                                            <input
                                                type="hidden"
                                                {...register(
                                                    `items.${index}.material_unit_id`,
                                                )}
                                            />
                                            <input
                                                type="hidden"
                                                {...register(
                                                    `items.${index}.price`,
                                                )}
                                            />
                                            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
                                                {/* Product Info */}
                                                <div className="flex flex-1 gap-4">
                                                    <Avatar
                                                        src={field.image_url}
                                                        name={
                                                            field.material_name
                                                        }
                                                        showFallback
                                                        radius="md"
                                                        size="lg"
                                                        className="bg-default-100 shrink-0"
                                                        fallback={
                                                            <NoImageFallback
                                                                iconSize={24}
                                                            />
                                                        }
                                                    />
                                                    <div className="space-y-1">
                                                        <ThemedText className="font-semibold">
                                                            {
                                                                field.material_name
                                                            }
                                                        </ThemedText>
                                                        <div className="flex flex-wrap items-center gap-2 text-xs">
                                                            <ThemedText
                                                                variant="secondary"
                                                                size="xs"
                                                            >
                                                                Harga:{" "}
                                                                {formatPrice(
                                                                    field.price,
                                                                )}
                                                            </ThemedText>
                                                            <ThemedText
                                                                variant="secondary"
                                                                size="xs"
                                                            >
                                                                •
                                                            </ThemedText>
                                                            <ThemedText
                                                                size="xs"
                                                                className=""
                                                            >
                                                                Terjual:{" "}
                                                                <span className="font-medium">
                                                                    {qtySold}{" "}
                                                                    {
                                                                        field.material_unit_name
                                                                    }
                                                                </span>
                                                            </ThemedText>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Input Controls */}
                                                <div className="flex flex-col gap-3 md:w-64">
                                                    <div className="flex items-end gap-2">
                                                        <Controller
                                                            control={control}
                                                            name={`items.${index}.quantity`}
                                                            rules={{
                                                                min: {
                                                                    value: 0,
                                                                    message:
                                                                        "Min 0",
                                                                },
                                                            }}
                                                            render={({
                                                                field: qtyField,
                                                            }) => (
                                                                <NumberInput
                                                                    {...qtyField}
                                                                    label="Jumlah Retur"
                                                                    size="sm"
                                                                    variant="bordered"
                                                                    minValue={0}
                                                                    maxValue={
                                                                        qtySold
                                                                    }
                                                                    hideStepper
                                                                    placeholder="0"
                                                                    className="flex-1"
                                                                    classNames={{
                                                                        input: "text-right",
                                                                    }}
                                                                    onChange={(
                                                                        value,
                                                                    ) =>
                                                                        qtyField.onChange(
                                                                            value,
                                                                        )
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </div>

                                                    {isActive && (
                                                        <div className="animate-in fade-in slide-in-from-top-1 flex flex-col gap-3 duration-200">
                                                            <Controller
                                                                control={
                                                                    control
                                                                }
                                                                name={`items.${index}.warehouse_id`}
                                                                rules={{
                                                                    required:
                                                                        isActive
                                                                            ? "Pilih gudang"
                                                                            : false,
                                                                }}
                                                                render={({
                                                                    field: whField,
                                                                    fieldState,
                                                                }) => (
                                                                    <Select
                                                                        isRequired
                                                                        label="Gudang Tujuan"
                                                                        placeholder="Pilih gudang"
                                                                        variant="bordered"
                                                                        size="sm"
                                                                        selectedKeys={
                                                                            whField.value
                                                                                ? [
                                                                                      String(
                                                                                          whField.value,
                                                                                      ),
                                                                                  ]
                                                                                : []
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) =>
                                                                            whField.onChange(
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                            )
                                                                        }
                                                                        isInvalid={
                                                                            !!fieldState.error
                                                                        }
                                                                    >
                                                                        {(
                                                                            warehouses ||
                                                                            []
                                                                        ).map(
                                                                            (
                                                                                wh,
                                                                            ) => (
                                                                                <SelectItem
                                                                                    key={
                                                                                        wh.value
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        wh.title
                                                                                    }
                                                                                </SelectItem>
                                                                            ),
                                                                        )}
                                                                    </Select>
                                                                )}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Summary Card (Mobile Only) */}
                <ReturnSummary
                    className="lg:hidden"
                    totalReturnAmount={totalReturnAmount}
                    activeItemCount={activeItemCount}
                    isValid={isValid}
                    isSaving={isSaving}
                    isSubmitting={isSubmitting}
                />
            </Form>

            <Modal
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                placement="center"
                size="xl"
                isDismissable={!isSaving}
                hideCloseButton={isSaving}
                classNames={{
                    header: "border-b border-default-100",
                    footer: "border-t border-default-100",
                }}
            >
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">
                                <ThemedText>Konfirmasi Retur</ThemedText>
                            </ModalHeader>
                            <ModalBody className="py-6">
                                <div className="flex flex-col gap-4">
                                    <ThemedText variant="secondary" size="sm">
                                        Mohon lengkapi alasan retur untuk setiap
                                        barang berikut:
                                    </ThemedText>

                                    <div className="flex max-h-[50vh] flex-col overflow-y-auto pr-2">
                                        {fields.map((field, index) => {
                                            const qty = Number(
                                                watchItems?.[index]?.quantity,
                                            );
                                            if (!qty || qty <= 0) return null;

                                            return (
                                                <div
                                                    key={field.id}
                                                    className="flex items-start gap-4 py-4 first:pt-0 last:pb-0"
                                                >
                                                    <div className="flex w-1/3 shrink-0 flex-col gap-1 pt-1">
                                                        <ThemedText
                                                            className="font-semibold"
                                                            size="sm"
                                                        >
                                                            {
                                                                field.material_name
                                                            }
                                                        </ThemedText>
                                                        <ThemedText
                                                            variant="secondary"
                                                            size="xs"
                                                        >
                                                            {qty}{" "}
                                                            {
                                                                field.material_unit_name
                                                            }{" "}
                                                            •{" "}
                                                            {formatCurrency(
                                                                calcReturnLineAmount(
                                                                    qty,
                                                                    field.price,
                                                                ),
                                                            )}
                                                        </ThemedText>
                                                    </div>

                                                    <div className="flex-1">
                                                        <Controller
                                                            control={control}
                                                            name={`items.${index}.description`}
                                                            render={({
                                                                field: descField,
                                                            }) => (
                                                                <Textarea
                                                                    {...descField}
                                                                    placeholder="Alasan retur (Opsional)..."
                                                                    variant="bordered"
                                                                    size="sm"
                                                                    minRows={2}
                                                                    classNames={{
                                                                        inputWrapper:
                                                                            "bg-white dark:bg-zinc-900 shadow-none",
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <div className="border-default-300 flex justify-between border-t border-dashed pt-4">
                                        <ThemedText
                                            variant="secondary"
                                            size="sm"
                                        >
                                            Total Estimasi
                                        </ThemedText>
                                        <ThemedText
                                            size="sm"
                                            className="font-bold"
                                        >
                                            {formatCurrency(totalReturnAmount)}
                                        </ThemedText>
                                    </div>
                                </div>
                            </ModalBody>
                            <ModalFooter>
                                <Button
                                    variant="flat"
                                    onPress={onClose}
                                    size="sm"
                                >
                                    Batal
                                </Button>
                                <Button
                                    color="primary"
                                    onPress={handleConfirm}
                                    isLoading={isSaving}
                                    size="sm"
                                    className="font-medium"
                                >
                                    Ya, Simpan Retur
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </div>
    );
};

export default SalesOrderReturnCreate;
